export type BotProfile = {
  name: string;
  shortName: string;
  behaviour: string;
  scoring: string;
};

export type ResearchReference = {
  label: string;
  href: string;
};

export type BotArticle = {
  id: string;
  name: string;
  accent: string;
  accent2: string;
  abstract: string;
  gameWorks: string[];
  terms: Array<{ term: string; definition: string }>;
  profiles: BotProfile[];
  equations: Array<{
    title: string;
    formula: string;
    explanation: string;
    symbols: string[];
  }>;
  workedExample: string;
  limitations: string[];
  references: ResearchReference[];
};

export const botArticles: BotArticle[] = [
  {
    id: "tic-tac-toe",
    name: "Tic Tac Toe",
    accent: "#2f8cff",
    accent2: "#2ccaf5",
    abstract:
      "Tic Tac Toe is a small, deterministic, perfect-information game. That makes it an ideal place to explain minimax: the bot can inspect every legal future rather than estimate from a sample.",
    gameWorks: [
      "Two players alternate placing X and O on an empty 3 × 3 grid.",
      "A player wins by occupying all three cells in any row, column, or diagonal.",
      "If all nine cells are occupied without a line, the game is a draw. There is no hidden information and no random event.",
    ],
    terms: [
      { term: "State", definition: "The board, whose turn it is, and whether the game has ended." },
      {
        term: "Game tree",
        definition:
          "Every legal move from a state, followed by every legal reply, until terminal boards.",
      },
      {
        term: "Utility",
        definition:
          "A numerical value assigned to an outcome: positive for a win, zero for a draw, negative for a loss.",
      },
      {
        term: "Minimax",
        definition:
          "Choose the move with the best outcome after assuming the opponent always chooses the reply worst for us.",
      },
    ],
    profiles: [
      {
        name: "Uniform random",
        shortName: "Random",
        behaviour:
          "Samples every empty cell with equal probability. It is a deliberately weak control policy.",
        scoring: "P(a) = 1 / |A(s)| for every legal action a.",
      },
      {
        name: "Easy deterministic",
        shortName: "Easy",
        behaviour:
          "Selects a repeatable legal cell pattern. Useful for predictable practice, not optimal play.",
        scoring: "A fixed index function chooses one member of the legal-action set.",
      },
      {
        name: "Medium tactical",
        shortName: "Medium",
        behaviour:
          "Wins immediately, blocks an immediate loss, then values corners and the centre.",
        scoring: "Immediate terminal tactics dominate positional preferences.",
      },
      {
        name: "Hard minimax",
        shortName: "Hard",
        behaviour: "Searches every continuation and chooses a move that cannot be forced to lose.",
        scoring: "Terminal wins score 10 − d, losses d − 10, and draws 0, where d is search depth.",
      },
    ],
    equations: [
      {
        title: "Minimax value",
        formula: "V(s) = maxₐ minᵦ V(T(T(s,a),b))",
        explanation:
          "At state s, evaluate each action a against the opponent reply b that hurts us most. T is the legal transition function.",
        symbols: [
          "s: current board",
          "a: our legal move",
          "b: opponent reply",
          "T: apply one legal move",
          "V: outcome value",
        ],
      },
      {
        title: "Depth-aware terminal score",
        formula: "U = 10 − d (win), 0 (draw), d − 10 (loss)",
        explanation:
          "Subtracting depth prefers a quicker win and delays an unavoidable loss without changing which outcomes are preferable.",
        symbols: ["U: terminal utility", "d: number of search edges from the current position"],
      },
    ],
    workedExample:
      "If X can win now, that child state has positive terminal utility. Any slower line has a smaller depth-adjusted value, while any move allowing O to force a win receives a negative minimax value. Hard mode therefore takes the immediate win.",
    limitations: [
      "Perfect Tic Tac Toe quickly converges to draws against another perfect policy.",
      "Win rate against a random policy measures exploitation of mistakes, not intelligence in a broad sense.",
    ],
    references: [
      {
        label: "NIST Dictionary of Algorithms and Data Structures: minimax",
        href: "https://xlinux.nist.gov/dads/HTML/minimax.html",
      },
      { label: "Topzle measured bot study", href: "/blog/experiments/tic-tac-toe" },
    ],
  },
  {
    id: "chess",
    name: "Chess",
    accent: "#ffc247",
    accent2: "#ff647c",
    abstract:
      "Chess has perfect information but an enormous search space. Topzle first asks chess.js for legal moves, then uses bounded search and evaluation. The research policies change strategic preferences while sharing the same rules engine.",
    gameWorks: [
      "Each side starts with sixteen pieces. Players alternate one legal move, with White moving first.",
      "Checkmate wins. Stalemate, insufficient material, repetition, and move-count rules can produce draws. Castling, en passant, and promotion are handled by the rules engine.",
      "A move that leaves the moving side's king in check is illegal and never reaches the scoring stage.",
    ],
    terms: [
      {
        term: "Ply",
        definition: "One move by one side. A full move contains a White ply and a Black ply.",
      },
      {
        term: "Evaluation function",
        definition: "An estimate of which side is favoured when search stops before checkmate.",
      },
      { term: "Mobility", definition: "The number of legal moves available in a position." },
      {
        term: "Alpha–beta pruning",
        definition:
          "A minimax optimisation that skips branches unable to change the selected result.",
      },
      {
        term: "Paired opening",
        definition:
          "The same opening position played twice with policy colours reversed to reduce colour and opening bias.",
      },
    ],
    profiles: [
      {
        name: "Aggressive pressure",
        shortName: "Aggressive",
        behaviour:
          "Rewards captures, checks, advanced pawns, and positions that restrict the opponent.",
        scoring:
          "Material + 1.4 × captured value + 180 × check − 1.5 × opponent mobility + pawn pressure.",
      },
      {
        name: "Defensive safety",
        shortName: "Defensive",
        behaviour:
          "Prioritises castling, development, material preservation, and reducing the opponent's legal choices.",
        scoring:
          "Base evaluation + 320 × castle − 4 × opponent mobility + 18 × developed minor pieces.",
      },
      {
        name: "Strategic development",
        shortName: "Strategic",
        behaviour:
          "Values centre occupation, developed bishops and knights, and sustainable pawn progress.",
        scoring:
          "Base evaluation + 38 × centre control + 42 × developed minor pieces + 9 × pawn advance.",
      },
      {
        name: "Uniform legal move",
        shortName: "Random",
        behaviour:
          "Chooses uniformly from legal chess moves. It provides a sanity-check baseline rather than realistic opposition.",
        scoring: "P(a) = 1 / |A(s)| over the legal move set.",
      },
    ],
    equations: [
      {
        title: "Static position value",
        formula: "E(s) = M(s) + P(s) + μ(s) + K(s)",
        explanation:
          "The evaluation adds material M, positional terms P, mobility μ, and king-safety/check term K. Positive values favour the bot colour.",
        symbols: [
          "M: signed piece values",
          "P: centre and development bonuses",
          "μ: legal-move contribution",
          "K: check and king-safety contribution",
        ],
      },
      {
        title: "Alpha–beta decision",
        formula: "a* = arg maxₐ minᵦ E(T(T(s,a),b))",
        explanation:
          "Hard production play looks through the opponent's best immediate reply. Alpha and beta bounds discard branches that cannot beat the best line already found.",
        symbols: [
          "a*: selected move",
          "a: candidate move",
          "b: opponent reply",
          "E: leaf evaluation",
        ],
      },
    ],
    workedExample:
      "A queen capture may look attractive to the aggressive profile, but it is rejected if the legal reply wins more material or checkmates. The defensive profile can instead prefer castling because its safety bonus and reduced reply set outweigh a small pawn gain.",
    limitations: [
      "These are shallow, interpretable policies, not claims of grandmaster strength.",
      "A fixed ply cap converts unfinished positions into capped draws, so the study compares bounded policies rather than classical tournament chess.",
      "Four random opening plies broaden positions but do not replace a curated opening suite.",
    ],
    references: [
      {
        label: "Claude Shannon, A Chess-Playing Machine",
        href: "https://www.scientificamerican.com/article/a-chess-playing-machine/",
      },
      {
        label: "Stockfish documentation: evaluation, search, and testing",
        href: "https://official-stockfish.github.io/docs/stockfish-wiki/Advanced-topics.html",
      },
      {
        label: "chess.js legal move and game-state library",
        href: "https://github.com/jhlywa/chess.js",
      },
      { label: "Topzle measured chess study", href: "/blog/experiments/chess" },
    ],
  },
  {
    id: "ludo",
    name: "Classic Indian Ludo",
    accent: "#2ed69b",
    accent2: "#ffc247",
    abstract:
      "Ludo combines dice uncertainty with tactical choices. A policy cannot control a fair die, but after a roll it can compare legal gotis using progress, captures, safety, threats, blockades, and the exact-roll home rule.",
    gameWorks: [
      "Each player races four gotis from the yard, around the shared track, into a colour-specific home lane, and finally home.",
      "A six can release a goti and usually grants another roll. Three consecutive sixes forfeit the sequence in Topzle's configured Indian rules.",
      "Landing on an opposing single goti captures it unless the square is safe. Exact movement is required to reach home, and configured rooms require a capture before entering the home lane.",
    ],
    terms: [
      { term: "Goti", definition: "One playing token." },
      {
        term: "Safe square",
        definition: "A marked track square on which an opposing goti cannot capture the occupant.",
      },
      {
        term: "Blockade",
        definition:
          "Two same-colour gotis sharing a legal track square and blocking passage under the configured rules.",
      },
      { term: "Policy", definition: "The rule used to rank legal goti moves after a dice result." },
      {
        term: "Stochastic",
        definition:
          "Containing randomness; repeated games can differ even when policies are unchanged.",
      },
    ],
    profiles: [
      {
        name: "Easy legal baseline",
        shortName: "Easy",
        behaviour: "Chooses a legal action using a simple repeatable order.",
        scoring: "Deterministic legal-action index.",
      },
      {
        name: "Balanced",
        shortName: "Balanced",
        behaviour:
          "Trades off safe progress, threat escape, captures, and bringing additional gotis into play.",
        scoring: "Weighted sum with moderate capture and risk coefficients.",
      },
      {
        name: "Aggressive",
        shortName: "Aggressive",
        behaviour: "Prioritises captures, pursuit distance, and extra-turn opportunities.",
        scoring: "Large capture reward plus smaller progress and safety terms.",
      },
      {
        name: "Defensive",
        shortName: "Defensive",
        behaviour: "Prioritises safe squares, blockades, escaping threats, and low exposure.",
        scoring: "Large safety and threat-reduction rewards minus exposure risk.",
      },
      {
        name: "Hard tactical",
        shortName: "Hard",
        behaviour:
          "Combines capture value, endangered-goti rescue, home probability, blockades, and endgame pressure.",
        scoring: "A richer utility function over every legal goti.",
      },
    ],
    equations: [
      {
        title: "Move utility",
        formula: "U(m) = wₚP + w꜀C + wₛS + wᵦB + wₕH − wᵣR",
        explanation:
          "Each legal move m receives a weighted score. Profiles differ mainly through weights, so the same feature can matter more to an aggressive or defensive bot.",
        symbols: [
          "P: forward progress",
          "C: capture value",
          "S: safety/threat reduction",
          "B: blockade value",
          "H: home/finish value",
          "R: exposure risk",
          "w: profile-specific weight",
        ],
      },
      {
        title: "Weighted dice probability",
        formula: "P(D = k) = wₖ / Σᵢ₌₁⁶ wᵢ",
        explanation:
          "This applies only when the clearly labelled Smart Dice mode is enabled. Fair Dice sets every weight to one, giving exactly 1/6 per face.",
        symbols: ["D: dice result", "k: face 1 through 6", "wₖ: positive weight for face k"],
      },
    ],
    workedExample:
      "After rolling 4, one goti can capture, another can enter a safe square, and a third only advances. Aggressive play usually values the capture most. Defensive play can choose the safe square if the capturing goti would become highly exposed.",
    limitations: [
      "Chance creates wide outcome variation, so thousands of games are more informative than a few demonstrations.",
      "Results apply to the stated Indian rules and dice mode; changing extra turns, blockades, or capture-before-home changes the game.",
    ],
    references: [
      { label: "Topzle Smart Dice disclosure", href: "/blog/smart-ludo-dice" },
      { label: "Topzle measured Ludo study", href: "/blog/experiments/ludo" },
    ],
  },
  {
    id: "connect-four",
    name: "Connect 4",
    accent: "#ff647c",
    accent2: "#ffc247",
    abstract:
      "Connect 4 is a deterministic alignment game with gravity. Search must recognise immediate wins and blocks, but stronger play also values playable threats, forks, central columns, and the opponent's best reply.",
    gameWorks: [
      "Players alternate dropping a disc into one of seven non-full columns on a 7 × 6 board.",
      "Gravity places the disc in the lowest empty cell. Four connected discs horizontally, vertically, or diagonally win.",
      "When all 42 cells fill without a four, the game is a draw.",
    ],
    terms: [
      {
        term: "Window",
        definition: "Any four consecutive cells that could become a winning line.",
      },
      {
        term: "Playable threat",
        definition: "An empty winning cell that can legally receive the next disc under gravity.",
      },
      {
        term: "Fork",
        definition: "A position creating two winning threats that cannot both be blocked.",
      },
      {
        term: "Search depth",
        definition: "How many future plies the bot inspects before using its evaluation function.",
      },
    ],
    profiles: [
      {
        name: "Uniform random",
        shortName: "Random",
        behaviour: "Samples among non-full columns equally.",
        scoring: "P(c) = 1 / number of legal columns.",
      },
      {
        name: "Easy pattern",
        shortName: "Easy",
        behaviour: "Uses a repeatable legal-column order for accessible practice.",
        scoring: "Fixed preference order constrained to legal columns.",
      },
      {
        name: "Medium search",
        shortName: "Medium",
        behaviour: "Finds immediate tactics and searches a bounded game tree.",
        scoring: "Alpha–beta search with centre and four-cell-window evaluation.",
      },
      {
        name: "Hard deeper search",
        shortName: "Hard",
        behaviour: "Searches further and punishes opposing forks more heavily.",
        scoring: "Deeper alpha–beta minimax over the same legal transition model.",
      },
    ],
    equations: [
      {
        title: "Window evaluation",
        formula: "E(W) = r₄I₄ + r₃I₃ + r₂I₂ − q₃J₃ − q₄J₄",
        explanation:
          "For each four-cell window W, I counts our patterns and J counts opponent patterns. A playable three receives much more weight than an isolated pair.",
        symbols: [
          "Iₖ: our k-disc pattern",
          "Jₖ: opponent k-disc pattern",
          "r and q: reward and penalty coefficients",
        ],
      },
      {
        title: "Search choice",
        formula: "c* = arg max꜀ minᵣ E(T(T(s,c),r))",
        explanation: "Choose column c while assuming the opponent reply r minimises our outcome.",
        symbols: ["c*: selected column", "r: legal reply", "T: gravity-aware transition"],
      },
    ],
    workedExample:
      "If column 4 wins immediately, every search profile chooses it. If the opponent wins next in column 2, blocking column 2 dominates a centre preference. With neither tactic present, central control and future playable threes separate moves.",
    limitations: [
      "Search depth is bounded for responsive browser and edge execution.",
      "A head-to-head result depends on the exact depth and evaluation coefficients and should not be generalised to all Connect 4 engines.",
    ],
    references: [
      {
        label: "Victor Allis, A Knowledge-based Approach of Connect-Four",
        href: "https://webdocs.cs.ualberta.ca/~jonathan/PREVIOUS/Courses/657/Notes.Vanessa/connect4.pdf",
      },
      { label: "Topzle measured Connect 4 study", href: "/blog/experiments/connect-four" },
    ],
  },
  {
    id: "battleship",
    name: "Battleship",
    accent: "#2ccaf5",
    accent2: "#2ed69b",
    abstract:
      "Battleship is a hidden-information search game. Topzle separates two decisions that are often confused: where to place a fleet and where to fire. Strong targeting scores every untried cell by counting legal remaining ship placements.",
    gameWorks: [
      "Each player hides ships of lengths 5, 4, 3, 3, and 2 on a 10 × 10 grid without overlap.",
      "Players alternate firing at an untried enemy cell and learn hit or miss. A ship sinks when all its cells are hit.",
      "The first player to sink all five opposing ships wins.",
    ],
    terms: [
      {
        term: "Hidden information",
        definition:
          "The opponent's ship cells are unknown; the bot sees only its own fleet and public hit/miss results.",
      },
      {
        term: "Hunt mode",
        definition: "After a hit, test adjacent cells and infer orientation to finish that ship.",
      },
      {
        term: "Probability density",
        definition: "For each cell, count remaining legal ship placements that pass through it.",
      },
      {
        term: "Paired fleet",
        definition:
          "Reuse equivalent hidden layouts while reversing who starts, reducing placement luck in a comparison.",
      },
    ],
    profiles: [
      {
        name: "Uniform fire",
        shortName: "Random",
        behaviour: "Chooses uniformly from cells not previously fired upon.",
        scoring: "P(i) = 1 / |U| for untried-cell set U.",
      },
      {
        name: "Hunt and target",
        shortName: "Hunt",
        behaviour:
          "Uses a spread pattern until a hit, then probes neighbours and continues along a discovered axis.",
        scoring: "Unresolved hit neighbours receive priority over search cells.",
      },
      {
        name: "Probability grid",
        shortName: "Probability",
        behaviour:
          "Enumerates placements consistent with public evidence and fires where the most placements overlap.",
        scoring: "D(i) = Σₗ Σₚ 1[i ∈ p ∧ p is evidence-consistent].",
      },
      {
        name: "Defensive placement",
        shortName: "Probability + defence",
        behaviour:
          "Retains probability targeting but spaces ships and varies orientation to reduce predictable clusters.",
        scoring: "Placement utility penalises adjacency and common high-density attack zones.",
      },
    ],
    equations: [
      {
        title: "Cell density",
        formula: "D(i) = Σₛ Σₚ∈Pₛ 𝟙(i ∈ p) · 𝟙(p agrees with evidence)",
        explanation:
          "Count every legal placement p for each unsunk ship s that contains cell i and does not contradict known misses or required hits.",
        symbols: [
          "i: candidate target cell",
          "Pₛ: legal placements of ship s",
          "𝟙: indicator, one when condition is true",
        ],
      },
      {
        title: "Shot accuracy",
        formula: "accuracy = hits / shots",
        explanation:
          "Accuracy is useful but not sufficient. The primary game outcome remains win/loss; average total shots describes search efficiency.",
        symbols: ["hits: shots striking a ship cell", "shots: all legal attacks made"],
      },
    ],
    workedExample:
      "A hit at E5 makes D5, F5, E4, and E6 immediate hunt candidates. After a second horizontal hit at F5, legal vertical placements disappear and the policy extends along the row. Between hits, the density grid favours cells contained in many possible ship placements.",
    limitations: [
      "The production bot observes only hit/miss history, never hidden enemy ship coordinates.",
      "Paired randomness reduces but does not remove every interaction between fleet placement, starting turn, and adaptive targeting.",
      "Accuracy may rise while win rate falls if hits are distributed inefficiently across ships.",
    ],
    references: [
      {
        label: "Hasbro Battleship game instructions",
        href: "https://instructions.hasbro.com/en-au/instruction/battleship",
      },
      {
        label: "Optimal Strategies on Attack and Defense in Battleship",
        href: "https://www.actonscholars.org/optimal-strategies-on-attack-and-defense-in-the-game-of-battleship/",
      },
      {
        label: "Compton, Liu & Stanzione: Battleship AI and Q-learning",
        href: "https://cs.uml.edu/ecg/uploads/AIfall14/compton_liu_stanzione.pdf",
      },
      { label: "Topzle measured Battleship study", href: "/blog/experiments/battleship" },
    ],
  },
  {
    id: "draw-guess",
    name: "Draw & Guess",
    accent: "#ffc247",
    accent2: "#2ccaf5",
    abstract:
      "Draw & Guess is a local image-classification problem. Strokes become a normalised bitmap and the small on-device classifier ranks labels without sending the drawing to an external AI service.",
    gameWorks: [
      "A player draws a secret prompt while other players guess.",
      "In local AI mode, the browser normalises the drawing and ranks known labels.",
      "A correct label within the round time completes the challenge.",
    ],
    terms: [
      { term: "Class", definition: "One possible drawing label, such as cat or bicycle." },
      {
        term: "Logit",
        definition:
          "A model's unnormalised score for a class before converting scores to probabilities.",
      },
      {
        term: "Top-k",
        definition:
          "Success when the correct label appears among the k highest-ranked predictions.",
      },
      {
        term: "Held-out set",
        definition: "Drawings never used for training or tuning, reserved for final evaluation.",
      },
    ],
    profiles: [
      {
        name: "Top-1 local classifier",
        shortName: "Top-1",
        behaviour: "Returns the single highest-ranked known label.",
        scoring: "ŷ = arg max꜀ z꜀, where z is the class-logit vector.",
      },
      {
        name: "Top-k suggestions",
        shortName: "Top-k",
        behaviour: "Keeps several plausible guesses as strokes accumulate.",
        scoring: "Select the k labels with greatest softmax probability.",
      },
    ],
    equations: [
      {
        title: "Softmax probability",
        formula: "P(c|x) = exp(z꜀) / Σⱼ exp(zⱼ)",
        explanation:
          "The model converts logits z for drawing x into comparable class probabilities.",
        symbols: ["x: normalised drawing", "c: candidate class", "z꜀: model score for class c"],
      },
      {
        title: "Per-class recall",
        formula: "recall꜀ = TP꜀ / (TP꜀ + FN꜀)",
        explanation:
          "Recall exposes classes the model often misses even when overall accuracy looks healthy.",
        symbols: [
          "TP: correctly recognised examples",
          "FN: examples of class c predicted as something else",
        ],
      },
    ],
    workedExample:
      "As a user sketches two wheels and a frame, bicycle may move from the top-five list to top-one. The model runs on pixels and learned features; it does not read the secret prompt.",
    limitations: [
      "No public experiment report is shown until a versioned, held-out drawing set has actually been evaluated.",
      "Accuracy can vary by device input, drawing style, class balance, and preprocessing.",
    ],
    references: [
      {
        label: "Ha & Eck, A Neural Representation of Sketch Drawings",
        href: "https://arxiv.org/abs/1704.03477",
      },
    ],
  },
  {
    id: "rock-paper-scissors",
    name: "Live Rock Paper Scissors",
    accent: "#ff647c",
    accent2: "#2f8cff",
    abstract:
      "Rock Paper Scissors is a simultaneous zero-sum game. A fair bot commits uniformly. An adaptive bot may study past gestures, but it must never observe the current locked choice before committing.",
    gameWorks: [
      "Each player independently commits rock, paper, or scissors.",
      "Rock beats scissors, scissors beats paper, and paper beats rock. Matching gestures draw.",
      "Choices reveal together so neither side can react to the other side's current move.",
    ],
    terms: [
      {
        term: "Mixed strategy",
        definition: "A probability distribution over several actions rather than one fixed action.",
      },
      { term: "Zero-sum", definition: "One player's gain equals the other player's loss." },
      {
        term: "Nash equilibrium",
        definition:
          "A strategy profile where no player benefits by changing alone. Here it is uniform 1/3 play.",
      },
      {
        term: "Information leak",
        definition:
          "Seeing the current opponent choice before committing, which invalidates fair play and evaluation.",
      },
    ],
    profiles: [
      {
        name: "Uniform random",
        shortName: "Uniform",
        behaviour: "Commits each gesture with probability one third.",
        scoring: "P(R) = P(P) = P(S) = 1/3.",
      },
      {
        name: "Frequency counter",
        shortName: "Frequency",
        behaviour: "Counters the gesture most common in the opponent's history.",
        scoring: "Choose the action beating arg max gesture count.",
      },
      {
        name: "Transition counter",
        shortName: "Transition",
        behaviour: "Estimates the next gesture from transitions following recent gestures.",
        scoring: "Choose the counter to arg maxⱼ P(next=j | recent state).",
      },
    ],
    equations: [
      {
        title: "Expected score",
        formula: "E[U] = Σᵢ Σⱼ pᵢ qⱼ Uᵢⱼ",
        explanation:
          "Multiply each outcome value by both players' action probabilities. Against uniform play, every fixed strategy has expected value zero.",
        symbols: [
          "p: our mixed strategy",
          "q: opponent strategy",
          "U: payoff matrix with win +1, draw 0, loss −1",
        ],
      },
    ],
    workedExample:
      "If a player chooses rock unusually often, a frequency bot can favour paper. If that player notices and changes, the advantage disappears; this is why evaluation needs held-out sequences and an exploitability check.",
    limitations: [
      "No experiment page is published because a locked, held-out sequence benchmark has not yet been run.",
      "Camera classification quality and game-strategy quality are separate measurements.",
    ],
    references: [
      {
        label: "Stanford Encyclopedia of Philosophy: game theory",
        href: "https://plato.stanford.edu/entries/game-theory/",
      },
    ],
  },
  {
    id: "snakes-ladders",
    name: "Snakes & Ladders",
    accent: "#2ed69b",
    accent2: "#ff647c",
    abstract:
      "Standard Snakes & Ladders has dice uncertainty but almost no move choice. The honest mathematical model is an absorbing Markov chain, not a claim that one identical rolling policy is strategically smarter.",
    gameWorks: [
      "Players roll one fair six-sided die and move that many numbered squares.",
      "Landing at a ladder foot moves the goti to its top. Landing on a snake head moves it to the tail.",
      "The first goti to reach the final square under the configured exact-finish rule wins.",
    ],
    terms: [
      {
        term: "Markov chain",
        definition:
          "A stochastic process whose next-state distribution depends on the current state.",
      },
      {
        term: "Transition matrix",
        definition:
          "A table giving the probability of moving from each square to every other square.",
      },
      {
        term: "Absorbing state",
        definition: "The finished square, which cannot be left after it is reached.",
      },
      {
        term: "Expected turns",
        definition: "The long-run average number of rolls required to finish from a chosen square.",
      },
    ],
    profiles: [
      {
        name: "Rules-conformant fair roller",
        shortName: "Fair",
        behaviour: "Samples each die face equally and applies board transitions exactly.",
        scoring: "P(D=k)=1/6; there is no post-roll move choice to optimise.",
      },
    ],
    equations: [
      {
        title: "Expected remaining turns",
        formula: "t = 1 + Qt, therefore t = (I − Q)⁻¹1",
        explanation:
          "Q contains transition probabilities among unfinished squares. Solving the linear system gives expected rolls from every square.",
        symbols: [
          "t: expected remaining turns vector",
          "Q: transient transition matrix",
          "I: identity matrix",
          "1: one turn just taken",
        ],
      },
    ],
    workedExample:
      "From square 20, each die face initially has probability 1/6. If 23 is the foot of a ladder, the probability mass for rolling 3 transfers directly to the ladder top rather than remaining on 23.",
    limitations: [
      "No strategy experiment is published because identical no-choice policies would make a win-rate comparison misleading.",
      "A useful future report would validate transition probabilities and expected game length, not invent policy differences.",
    ],
    references: [
      {
        label: "Grinstead & Snell, Introduction to Probability: Markov chains",
        href: "https://math.dartmouth.edu/~prob/prob/prob.pdf",
      },
    ],
  },
];

export const botArticleById = new Map(botArticles.map((article) => [article.id, article]));
export const measuredExperimentIds = new Set([
  "tic-tac-toe",
  "chess",
  "ludo",
  "connect-four",
  "battleship",
]);
